/** Extract a string field from an unknown input -- the canonical replacement for the hand-rolled `typeof input.X === "string" ? input.X : ""` pattern. Strict-string semantics, NO coercion: a non-object input, an absent key, or a non-string value all return `defaultValue` (default `''`). The shared body `recordStringOr` in `@federa/const/record-guards` serves the same purpose but REQUIRES an explicit default at the call site. */

import { asRecordOrNull, recordStringOr, rowString } from '@federa/const/record-guards';

/** Return `input[key]` when it is a string; otherwise `defaultValue` (empty string by default). The default-optional
 * spelling of the shared `recordStringOr` body -- this leaf owns the CALL SHAPE, never a second copy of the read. */
export function extractStringInput(input: unknown, key: string, defaultValue: string = ''): string {
	return recordStringOr(input, key, defaultValue);
}

/** Nullable sibling of {@link extractStringInput}: `input[key]` when it is a string, else NULL -- the null-default
 * BIND contract (an omitted actor id binds NULL, never '') the ledger marker builders hand-rolled per site behind
 * rule-exemption comments before this arm existed.
 *
 * THE KEYED READ IS `rowString`'s, not a second copy of it (adopted 2026-09-05). This leaf owns exactly one decision
 * -- the `unknown` DOOR -- and `asRecordOrNull` is the const-homed coercer that opens it, so the guard/read/coerce
 * body that used to be spelled here is now spelled once, in `const/record-guards.ts`. The two agree on every input by
 * construction: `asRecordOrNull` IS `isRecordValue`, and `rowString`'s null arm answers the same `null` this used to
 * return before reaching its own read. */
export function extractStringOrNull(input: unknown, key: string): string | null {
	return rowString(asRecordOrNull(input), key);
}
